import { useCallback } from "react";
import { HttpError } from "@/api/client";
import { useT, type MessageVars } from "./I18nProvider";

/**
 * 서버 사유 코드 → 화면 문구 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * 서버는 **문장을 보내지 않습니다.** `reasonCode`와 유계 인자만 오고, 문구는 여기서
 * 고릅니다. 그래서 같은 실패가 요청 언어와 무관하게 같은 바이트로 나가고, 화면은
 * 사용자가 고른 언어로 말합니다.
 *
 * 아직 옮기지 않은 경로는 `reasonCode` 없이 `message`만 보냅니다. 그때는 서버 문구를
 * **그대로** 보여줍니다 — 화면이 사유를 지어내면 사용자가 읽는 것이 서버가 아는
 * 사실과 달라집니다.
 */

/** 화면이 문구를 아는 사유. 여기 없는 코드는 "알 수 없음"으로 접습니다. */
const REASON_MESSAGE_ID: Record<string, string> = {
  method_not_allowed: "common.error.reason.method_not_allowed",
  unauthorized: "common.error.reason.unauthorized",
  forbidden: "common.error.reason.forbidden",
  session_unavailable: "common.error.reason.session_unavailable",
  internal: "common.error.reason.internal",
};

const UNKNOWN_REASON_ID = "common.error.reason.unknown";

export function useApiErrorText(): (error: unknown) => string | undefined {
  const { t } = useT("common");
  return useCallback(
    (error: unknown) => {
      if (!(error instanceof HttpError)) return undefined;
      const { reasonCode, args, message, requestId } = error.body;
      if (!reasonCode) return message || undefined;
      const id = REASON_MESSAGE_ID[reasonCode];
      /* 서버 인자는 이미 서버 allowlist를 지났습니다. 여기서는 템플릿이 요구하는
         자리만 채우고, 나머지는 문구에 나타나지 않습니다. */
      const vars: MessageVars = { ...(args ?? {}), requestId };
      return id ? t(id, vars) : t(UNKNOWN_REASON_ID, vars);
    },
    [t],
  );
}
